import axios from 'axios';
import store from '@/store';
import moment from 'moment-timezone';
import eventBus from '@/components/shared/eventbus';
import globalErrorHandler from '@/components/shared/globalError';
import { fromLongToShort } from '@/components/shared/languageCodes';

const apiURL = process.env.VUE_APP_API_URL;
const appVersion = process.env.VUE_APP_APP_VERSION;
const appName = process.env.VUE_APP_APPLICATION_NAME;

const JSONbigNative = require('json-bigint')({ storeAsString: true });

axios.defaults.transformResponse = [
  (data) => {
    if (typeof data === 'string') {
      try {
        return JSONbigNative.parse(data);
      } catch (e) {
        console.error(e);
      }
    }
    return data;
  },
];

function extendConfigParams(oldConfig) {
  const config = { ...oldConfig };
  const user = store.state.users.loggedInUserProfile.user;
  const isDktMember = user && user.isDktMember;
  const isKHMember = user && user.isKozigMember;
  const isNeptunMember = user && user.isNeptunMember;
  const learnedLanguage = store.getters['settings/getSelectedLearnedLanguage'];
  const deviceName = store.getters['device/deviceName'];
  config.headers.platform = 'web';
  config.headers.app_name = appName;
  config.headers.os_version = navigator.platform;
  config.headers.client_time_with_zone = moment().format(
    'YYYY-MM-DD HH:mm:ssZ'
  );
  // eslint-disable-next-line no-control-regex
  config.headers.device_name = deviceName.replace(/[^\x00-\x7F]/g, '');
  if (isDktMember === true && !isKHMember && !isNeptunMember) {
    delete config.headers['Auth-Token'];
    const cookieToken = window.$cookies.get('api-token');
    config.headers['Api-Token'] = store.getters['users/getApiToken'];
    if (cookieToken) {
      config.headers['Api-Token'] = cookieToken;
    }
  }
  if (learnedLanguage) {
    config.headers['Accept-Content-Language'] = learnedLanguage;
  } else {
    config.headers['Accept-Content-Language'] = isKHMember ? 'enpam' : 'en';
  }

  if (store.getters['contents/getIsClassLanguageCollison']) {
    config.headers['Prevent-Learned-Language-Difference-Error'] = true;
    store.dispatch('contents/setSendedLanguageCollisonHeader', true);
  } else if (
    !store.getters['contents/getIsClassLanguageCollison'] &&
    store.getters['contents/getSendedLanguageCollisonHeader']
  ) {
    config.headers['Prevent-Learned-Language-Difference-Error'] = false;
  } else {
    config.headers['Prevent-Learned-Language-Difference-Error'] = false;
  }
  return config;
}

function isPlacementLessonEndpoint(endpointURL) {
  return endpointURL.includes('/contents/placement_test');
}

function fetchData(endpoint, requestType, requestBody, authTokenRequired) {
  let token = store.getters.getAuthToken;
  const timeZone = moment.tz.guess();
  const xLang = window.getParameterByName('lang');

  if (xLang && endpoint.includes('key=')) {
    token = undefined;
  }
  let config = {
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Time-Zone': timeZone,
      'App-Version': appVersion,
    },
  };

  if (authTokenRequired != undefined && authTokenRequired == false) {
    config = extendConfigParams(config);
  } else if (
    requestBody &&
    requestBody.chatBotPartner &&
    requestBody.chatBotPartner === 'xeropan_landing_page'
  ) {
    config.headers['Auth-Token'] = store.state.teachBotToken;
  } else if (
    requestBody &&
    Object.prototype.hasOwnProperty.call(requestBody, 'hash')
  ) {
    config.headers['Auth-Token'] = `${token}_${requestBody.hash}`;
  } else {
    config.headers['Auth-Token'] = token;
    config = extendConfigParams(config);
  }

  let shortLanguageCode;
  if (xLang) {
    if (xLang.length == 2) {
      shortLanguageCode = xLang;
    } else {
      shortLanguageCode = fromLongToShort[xLang];
    }
  } else if (
    store.state &&
    store.state.settings &&
    store.state.settings.userSelectedLanguage
  ) {
    shortLanguageCode =
      fromLongToShort[store.state.settings.userSelectedLanguage];
  }
  if (
    store.getters['settings/getSiteLanguage'] &&
    isPlacementLessonEndpoint(endpoint)
  ) {
    config.headers['Accept-Languages'] =
      fromLongToShort[store.getters['settings/getSiteLanguage']];
  } else if (shortLanguageCode) {
    config.headers['Accept-Languages'] = shortLanguageCode;
  } else {
    const htmlLang = document.documentElement.getAttribute('lang');
    config.headers['Accept-Languages'] = htmlLang;
  }

  const axiosConfig = {
    method: requestType.toLowerCase(),
    url: endpoint,
    headers: config.headers,
  };
  if (requestBody) {
    if (['get'].includes(requestType.toLowerCase())) {
      axiosConfig.params = requestBody;
    } else {
      axiosConfig.data = requestBody;
    }
  }

  return axios(axiosConfig)
    .then((response) => {
      globalErrorHandler(response);
      const sessionTimeLeft = response.headers['token-time-left'];
      const urlParams = new URLSearchParams(location.search);
      const isWithoutUser = urlParams.get('x-key');
      if (
        sessionTimeLeft &&
        !endpoint.includes('/logout') &&
        isWithoutUser == undefined
      ) {
        if (sessionTimeLeft < 0) {
          eventBus.sessionLeft(0);
        } else {
          eventBus.sessionLeft(sessionTimeLeft);
        }
      }
      if (
        requestBody &&
        Object.prototype.hasOwnProperty.call(requestBody, 'hash')
      ) {
        return response;
      }
      return response.data;
    })
    .catch((error) => {
      globalErrorHandler(error);
      return error;
    });
}

function googleTranslate(url) {
  return fetch(url, {
    cache: 'no-cache',
    headers: {
      'content-type': 'application/json',
      'cache-control': 'no-cache',
    },
    method: 'GET',
    mode: 'cors',
  })
    .then((response) => response.json())
    .catch((error) => console.log(error));
}

function getCurrencyExchange(url) {
  return fetch(url)
    .then((response) => response.json())
    .catch((error) => console.log(error));
}

function witAi(url, data) {
  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: 'Bearer IGIOUDF726JALHGJ7XKSFVW4VHGRUDTA',
      'Content-Type': 'audio/wav',
      'Transfer-encoding': 'chunked',
    },
    body: data,
  })
    .then((response) => response.json())
    .catch((error) => console.log(error));
}

function textToSpeech(data) {
  return fetch('https://texttospeech.googleapis.com/v1beta1/text:synthesize', {
    cache: 'no-cache',
    headers: {
      'content-type': 'application/json',
      'cache-control': 'no-cache',
      'X-Goog-Api-Key': 'AIzaSyDEOq7opVLic_ZU56pZkj4wwpfZw94YE98',
    },
    method: 'POST',
    mode: 'cors',
    body: JSON.stringify(data),
  })
    .then((response) => response.json())
    .catch((error) => console.log(error));
}

export {
  fetchData,
  googleTranslate,
  witAi,
  apiURL,
  textToSpeech,
  getCurrencyExchange,
};
